/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { css, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators";

import "@maicol07/material-web-additions/data-table/data-table";
import "@maicol07/material-web-additions/data-table/data-table-row";
import "@maicol07/material-web-additions/data-table/data-table-column";
import "@maicol07/material-web-additions/data-table/data-table-cell";

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";
import "@material/mwc-select";
import "@material/mwc-dialog";

import "@material/web/textfield/outlined-text-field";
import "@material/web/iconbutton/icon-button";

import { User } from "../data-classes/user";
import { HitlElement } from "../hitl-element";
import { fetchRetry } from "../fetch-retry";

/**
 * Component to manage users
 * Part of the admin page hitl-administration.ts
 */
@customElement("hitl-users")
export class HitlUsers extends HitlElement {
  
  /**
   * Selected users
   */
  @property({ type: Array })
  selectedUsers: User[] = [];

  /**
   * Boolean per default its false. When a new client should be created this value becomes true and makes the a new row visible, if the value persists as true. 
   */
  @property({ type: Boolean })
  addUser: boolean = false;

  /**
   * Gis user promise
   */
  gisUserPromise: Promise<void>;

  /**
   * Gis load okay
   */
  gisLoadOkay: (() => void) | undefined;

  /**
   * Gis load fail
   */
  gisLoadFail: ((error: any) => void) | undefined;
  /**
   * Dialog text
   */
  dialogText = document.createElement("p");

  /**
   * Ok button for dialog
   */
  footerButtonOk = document.createElement("mwc-button");

  /**
   * Cancel button for dialog
   */
  footerButtonCancel = document.createElement('mwc-button');

  /**
   * E-mail text for copy dialog
  */
  emailText = document.createElement("p");

  /**
   * Password text for copy dialog
   */
  passwordText = document.createElement("p");

  /**
   * Copy e-mail button
   */
  copyEmailButton = document.createElement("mwc-icon-button");

  /**
   * Copy password button
   */
  copyPasswordButton = document.createElement("mwc-icon-button");

  /**
   * Copy e-mail tip div tag
   */
  emailTip = document.createElement("div");

  /**
   * Copy password tip div tag
   */
  passwordTip = document.createElement("div");

  /**
   * Copy e-mail tip text
   */
  emailTipText = document.createElement("span");

  /**
   * Copy password tip text
   */
  passwordTipText = document.createElement("span");

  /**
   * Copy e-mail div tag
   */
  emailDiv = document.createElement("div");

  /**
   * Copy password div tag
   */
  passwordDiv = document.createElement("div");
  
  /**
   * Temp selected rows
   */
  @property({ type: Array })
  tempSelectedRows = [];

  /**
   * Timer id for connected- and callback
   */
  timerId: any;

  /**
   * Admin group id. It will readed from the file .env
   */
  ADMIN_GROUP_ID = process.env.ADMIN_GROUP_ID;

  /**
   * User group id. It will readed from the file .env
   */
  USER_GROUP_ID = process.env.USER_GROUP_ID;

  /**
   * Customer id. It will readed from the file .env
   */
  CUSTOMER_ID = process.env.CUSTOMER_ID;

  /**
   * Organisation unit path. It will readed from the file .env. 
   * Depending on the project has to be changed in terraform.tfvars
   */
  ORG_UNIT_PATH = process.env.ORG_UNIT_PATH;

  static override styles = css`
    .mdc-card > div {
      margin: 0px 12px 0 12px;
    }

    .mdc-card__actions {
      gap: 12px;
    }

    md-data-table-footer {
      gap: 10px;
      align-items: center;
    }

    md-outlined-text-field {
      padding: 10px 0 10px;
      width: 100%;
    }

    mwc-select {
      width: 100%;
    }
    md-data-table-cell {
      background-color: #ffffff;
    }
    md-data-table-column {
      background-color: #ffffff;
    }
    md-data-table-footer {
      background-color: #ffffff;
    }
    .capitalize {
      text-transform: capitalize;
    }
    .tooltip {
        position: relative;
        display: inline-block;
        border-bottom: 1px dotted black;
      }
      
      .tooltip .tooltiptext {
        visibility: hidden;
        width: 120px;
        background-color: #555;
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 5px 0;
        position: absolute;
        z-index: 1;
        bottom: 125%;
        left: 50%;
        margin-left: -60px;
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      .tooltip .tooltiptext::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #555 transparent transparent transparent;
      }
      
      .tooltip:hover .tooltiptext {
        visibility: visible;
        opacity: 1;
      }
  `;

  constructor() {
    super();
    this.gisUserPromise = new Promise((resolve, reject) => {
      this.gisLoadOkay = resolve;
      this.gisLoadFail = reject;
    });
  }

  protected override async firstUpdated() {
    const accessToken = await this.getAccessToken();
    //Initialization users
    if(this.database){
      this.users = await User.list(this.database, accessToken);
      console.log("users", this.users);
    }
  }

  override render() {
    const template: TemplateResult[] = [];
    template.push(html`
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">Benutzer Administration</h2>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
          <md-data-table
            aria-label="Benutzer Administration"
            density=""
            page-sizes-label="Zeilen pro Seite:"
            first-row-of-page=""
            current-page-size=""
            last-row-of-page=""
            paginated="true"
            pagination-total-label=":firstRow-:lastRow of :totalRows"
            @row-selection-changed=${this.rowSelectionChanged}
            @selected-all=${this.rowSelectionChanged}
            @unselected-all=${this.rowSelectionChanged}>
            <md-data-table-column type="checkbox"></md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              E-Mail
            </md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Vorname
            </md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Nachname
            </md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Rolle
            </md-data-table-column>
            ${this.addUser
              ? html`<md-data-table-row data-id="-1">
                  <md-data-table-cell>
  <mwc-icon-button @click=${() =>
    (this.addUser =
      false)} icon="cancel"></mwc-icon-button></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell>
                  <md-outlined-text-field id="firstName" class="capitalize" label="Vorname" @keydown="${this.handleKey}">
                  </md-outlined-text-field>
                  </md-data-table-cell>
                  <md-data-table-cell><md-outlined-text-field id="lastName" class="capitalize" label="Nachname" @keydown="${this.handleKey}">
                    </md-outlined-text-field></md-data-table-cell>
                    </md-data-table-cell>
                  <md-data-table-cell><mwc-select id="role" label="Rolle" fixedMenuPosition=true>                    
                    <mwc-list-item value="USER" selected>USER</mwc-list-item>
                    <mwc-list-item value="ADMIN">ADMIN</mwc-list-item>
                    <mwc-list-item value="DATA-ADMIN">DATA-ADMIN</mwc-list-item>
                </mwc-select>
                </md-data-table-cell>
                </md-data-table-row>`
              : null}
            ${this.users.map((user: User) => {
              return html`
                <md-data-table-row data-id=${user.userID}>
                  <md-data-table-cell
                    type="checkbox"
                    class="user-checkbox"></md-data-table-cell>
                  <md-data-table-cell>${user.userID}</md-data-table-cell>  
                  <md-data-table-cell>${user.firstName}</md-data-table-cell>
                  <md-data-table-cell>${user.lastName}</md-data-table-cell>
                  <md-data-table-cell>${user.role}</md-data-table-cell>
                </md-data-table-row>
              `;
            })}
            <md-data-table-footer
              slot="footer"
              style="display: flex; align-items: left; justify-content: left;">
              <mwc-button
                raised
                @click=${() => (this.addUser = true)}
                label="Neuen Benutzer hinzufügen"
                ?disabled=${this.addUser}></mwc-button>
              <mwc-button
                raised
                @click=${this.saveUser}
                label="Neuen Benutzer speichern"
                ?disabled=${!this.addUser}></mwc-button>
              <mwc-button
                raised
                @click=${this.deleteUser}
                label="Ausgewählten Benutzer löschen"
                ?disabled=${this.addUser ||
                this.selectedUsers.length === 0}></mwc-button>
                <mwc-button
                raised
                @click=${this.generatePassword}
                label="Neues Passwort generieren"
                ?disabled=${this.addUser ||
                this.selectedUsers.length === 0 || this.selectedUsers.length > 1}></mwc-button>
            </md-data-table-footer>
          </md-data-table>
        </div>
      </div>
    `);
    return template;
  }

  /**
   * Special event from the package maicol07/material-web-additions/data-table/data-table.
   * It triggers if the row selection in the data-table changes
   * @param e 
   */
  rowSelectionChanged = async (e: CustomEvent) => {
    console.log("rowSelectionChanged", e);
    this.tempSelectedRows = e.target!.rows!.filter((row: any) => row.selected);
    this.selectedUsers = e
      .target!.rows!.filter((row: any) => row.selected)
      .map((row: any) =>
        this.users.find((user) => user.userID === row.dataset.id)
      );
    console.log("selectedUsers", this.selectedUsers);
  };

  override async connectedCallback() {
    super.connectedCallback();
    //Interval in which users are updated each minute = 60000 msec.
    this.timerId = setInterval(async () => {
      if (this.database) {
        const accessToken = await this.getAccessToken();
        this.users = await User.list(this.database, accessToken);
      }
      this.requestUpdate();
      console.log("requestUpdate called");
    }, 60000);
    await this.gisUserPromise;
    //Dialog click event
    this.dialog!.addEventListener("click", (event) => {
      const rect = this.dialog!.getBoundingClientRect();
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;
      if (!isInDialog) {
        this.dialog!.open = false;
      }
    });
  }

  //Interval will stopped by leaving the page administration
  override disconnectedCallback() {
    super.disconnectedCallback();
    //Interval for users get disconnected after leaving the page with this component
    if (this.timerId) clearInterval(this.timerId);
    console.log("Clear Interval from disconnectedCallback");
  }

  /** 
   * Handeling interaction with new consultant using keyboard keys
   * @param e KeyboardEvent is an event which describes a key action 
   * 
   */
  handleKey(e: KeyboardEvent){
    const firstName = (
      this.renderRoot.querySelector("#firstName") as HTMLInputElement
    );    
    const lastName = (
      this.renderRoot.querySelector("#lastName") as HTMLInputElement
    ); 
    if(e.key === "Enter"){
        if(firstName.value.trim() !== "" && lastName.value.trim() !== "") {
          this.saveUser(e as Event);
        } else {
          if(firstName.value.trim() == ""){
            firstName.focus();
          }else if(lastName.value.trim() == ""){
            lastName.focus();
          }
        }
        
    } else if(e.key == "Escape"){
        this.addUser = false;
    }
  }

  /**
   * Save new user
   * @param e 
   */
  saveUser = async (e: Event) => {
    const firstName = (
      this.renderRoot.querySelector("#firstName") as HTMLInputElement
    ).value;    
    const lastName = (
      this.renderRoot.querySelector("#lastName") as HTMLInputElement
    ).value; 
    //If first- and lastname are not empty
    if(firstName.length > 0 && lastName.length > 0){    
      const accessToken = await this.getAccessToken(
        true,
        "https://www.googleapis.com/auth/cloud-platform https://www.googleapis.com/auth/admin.directory.user.readonly https://www.googleapis.com/auth/admin.directory.user",
        false
      );
    /**
     * E-mail of the new user
     */
    const userID = (await this.emailGeneration(accessToken, firstName.toLowerCase(), lastName.toLowerCase())).toString();
    /**
     * Role of the new user
     */
    const role = (this.renderRoot.querySelector("#role") as HTMLInputElement)
      .value;
    /**
     * Stores the firstname with the first char as uppercase
     */
    let firstNameUppercase;
    /**
     * Stores the lastname with the first char as uppercase
     */
    let lastNameUppercase;
    //If firstname has a higher length than 1, then make the first char of the firstname uppercase
    if (firstName.length > 1) {
      firstNameUppercase =
        firstName.charAt(0).toUpperCase() + firstName.slice(1);
    //If firstname is only one symbol, then make the character uppercase    
    } else {
      firstNameUppercase = firstName.toUpperCase();
    }
    //If lastname has a higher length than 1, then make the first char of the lastname uppercase
    if (lastName.length > 1) {
      lastNameUppercase = lastName.charAt(0).toUpperCase() + lastName.slice(1);
    //If lastname is only one symbol, then make the character uppercase
    } else {
      lastNameUppercase = lastName.toUpperCase();
    }
    /**
     * The current group id will be stored in this var
     */
    let groupId;
    //If the new user has the role ADMIN or DATA-ADMIN then group id is equal admin group id
    if (role === "ADMIN" || role === "DATA-ADMIN") {
        groupId = this.ADMIN_GROUP_ID;
    //If the new user has the role USER then group id is equal user group id
    } else {
        groupId = this.USER_GROUP_ID;
    }
    /**
     * The new user which will be inserted in Spanner database
     */
    const user = new User(
    userID,
    firstNameUppercase,
    lastNameUppercase,
    role
    );
    /**
     * Initialpassword for the new User
     */
    const initialPassword = new Array(10).fill("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz").map(x => (function(chars) { let umax = Math.pow(2, 32), r = new Uint32Array(1), max = umax - (umax % chars.length); do { crypto.getRandomValues(r); } while(r[0] > max); return chars[r[0] % chars.length]; })(x)).join('');  
    //Generate user with password in admin.google
    await fetchRetry(
        `https://admin.googleapis.com/admin/directory/v1/users`,
        {
            //Should send the user an invitation
            method: "POST",
            headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            },
            body: JSON.stringify({
            name: {
                givenName: user.firstName,
                familyName: user.lastName,
            },
            password: initialPassword,
            primaryEmail: user.userID,
            changePasswordAtNextLogin: true,
            
            orgUnitPath: this.ORG_UNIT_PATH,    
            }),
        }
      );
      this.dialog!.innerHTML = `Benutzer erstellt.`;
      this.copyEmailButton.setAttribute("icon", "content_copy");
      this.copyEmailButton.addEventListener('click', ()=> this.copyClipboard(e, "E-Mail",userID));
      this.emailText.innerHTML=`E-Mail: ${userID}`;
      this.emailTip.classList.add("tooltip");
      this.emailTipText.classList.add("tooltiptext");
      this.emailTipText.innerHTML = "E-Mail kopieren";
      this.emailTip.appendChild(this.copyEmailButton);
      this.emailTip.appendChild(this.emailTipText);
      this.emailDiv.appendChild(this.emailText);
      this.emailDiv.appendChild(this.emailTip);
      
      this.passwordText.innerHTML=`Initialpasswort: ${initialPassword}`
      this.copyPasswordButton.setAttribute("icon", "content_copy");
      this.copyPasswordButton.addEventListener('click', ()=>this.copyClipboard(e, "Password",initialPassword));
      this.passwordTip.classList.add("tooltip");
      this.passwordTipText.classList.add("tooltiptext");
      this.passwordTipText.innerHTML = "Passwort kopieren";
      this.passwordTip.appendChild(this.copyPasswordButton);
      this.passwordTip.appendChild(this.passwordTipText);
      this.passwordDiv.appendChild(this.passwordText);
      this.passwordDiv.appendChild(this.passwordTip);
      this.footerButtonOk.setAttribute("slot", "primaryAction");
      this.footerButtonOk.setAttribute("dialogAction", "ok");
      this.footerButtonOk.innerText = "Ok";
      this.footerButtonOk.classList.add("raised");
      this.dialog!.appendChild(this.emailDiv);
      this.dialog!.appendChild(this.passwordDiv);
      this.dialog!.appendChild(this.footerButtonOk);                            
      this.dialog!.open = true;      
      this.dialog!.addEventListener('closed', ()=>{
        this.tempSelectedRows.forEach((row:any) =>{
            row.selected = false;
        });
        //TODO: Verschieben in andere Methode
        this.selectedUsers = [];
        this.dialog!.innerHTML = "";
        this.emailDiv.innerHTML = "";
        this.passwordDiv.innerHTML = "";
        this.emailText.innerHTML = "";
        this.emailTip.innerHTML = "";
        this.passwordText.innerHTML = "";
        this.passwordTip.innerHTML = "";
      });
      // wait 5 seconds before adding user to group
      await new Promise(resolve => setTimeout(resolve, 5000));
      // add user to group in admin.google
      const url = `https://cloudidentity.googleapis.com/v1/${groupId}/memberships`; //Add existing users to group
      await fetchRetry(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          "preferredMemberKey": {
            id: userID,
          },
          roles: [
            {
              name: "MEMBER",
            },
          ],
        }),
      })
        .then(async (response) => {
            console.log(response);
        })
        .catch((error) => console.error(error));

      user.save(this.database!, accessToken);
      this.users = this.users.concat([user]);
      this.addUser = false;
    } else {
        //Cases to assign dialog text a value
        switch(true) {
            //If firstname and lastname are empty
            case !firstName && !lastName:
                this.dialogText.innerHTML= "Geben Sie bitte den Vor- und Nachnamen des neuen Benutzers an";
                break;
            //If only firstname is empty
            case !firstName:
                this.dialogText.innerHTML= "Geben Sie bitte den Vornamen des neuen Benutzers an";
                break;
            //If only lastname is empty
            case !lastName:
                this.dialogText.innerHTML= "Geben Sie bitte den Nachnamen des neuen Benutzers an";
                break;
        }
        this.initDialog();
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        
        this.dialog!.open = true;
        this.footerButtonOk.addEventListener('click', ()=> {
            this.dialog!.open=false;
        });
    }
  };

  /**
   * Generate Email for new User
   * @param accessToken 
   * @param firstname 
   * @param lastname 
   * @returns Promise<string>
   */
  async emailGeneration(accessToken:string, firstname: string, lastname: string): Promise<string> {
    /**
     * List all users
     */
    let listUsers: string | any[] =[];
    /**
     * List all e-mails of the users from listUsers
     */
    const listEMails: any[] = [];

    const regexSpecialSymbols =/[äöü]/i;
    //Replace Ä, Ö and or Ü because emails cannot have these symbols
    if(regexSpecialSymbols.test(firstname)){
      firstname = firstname.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
               .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue');
    } else if(regexSpecialSymbols.test(lastname)){
      lastname = lastname.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
              .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue');
    }
    //Call to get list of users from admin.google
    await fetchRetry(
        `https://admin.googleapis.com/admin/directory/v1/users?customer=${this.CUSTOMER_ID}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      ).then(async (response) => {
        //If response is ok then the data will be loaded
        if (response.ok) {
          const data = await response.json();
          //Assign listUsers data.users 
          listUsers = data.users;
        } else {
          //If response is not ok then it gives an error response with the status and the status text
          throw new Error(`Error: ${response.status} - ${response.statusText}`);
        }
      }).catch((error) => {
        console.error(error);
      });
      listUsers.map((user, index) =>{
        //Each e-mail will assigned to listEMails with the same index
        listEMails[index] = user.primaryEmail;
      });
    /**
     * Counter for e-mails
     */
    let counter = 1;
    /**
     * Var for default email for the new user
     */
    let defaultEmail=`${firstname}.${lastname}@anydoc.ai`;
    //Check if E-Mail exists
    listEMails.forEach((email) =>{
        //If the e-mail exists then set a counter
        if(email == defaultEmail){
          defaultEmail = `${firstname}.${lastname}${counter}@anydoc.ai`
        //If e-mail even with counter exists increase value of counter
        } else if(email == `${firstname}.${lastname}${counter}@anydoc.ai`){
            counter++;
        }
    });
    return defaultEmail;
  }

  /**
   * Generate a new password for an existing user
   * @param e 
   */
  generatePassword = async (e: Event) => {
    const accessToken = await this.getAccessToken(
        true,
        "https://www.googleapis.com/auth/cloud-platform https://www.googleapis.com/auth/admin.directory.user",
        false
      );
      //If the user exists then set a new password
      if (this.users.find((user: User) => user.userID == this.selectedUsers[0].userID)) {
        const initialPassword = new Array(10).fill("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz").map(x => (function(chars) { let umax = Math.pow(2, 32), r = new Uint32Array(1), max = umax - (umax % chars.length); do { crypto.getRandomValues(r); } while(r[0] > max); return chars[r[0] % chars.length]; })(x)).join('');  
        await fetchRetry(
          `https://admin.googleapis.com/admin/directory/v1/users/${this.selectedUsers[0].userID}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              password: initialPassword,
              primaryEmail: this.selectedUsers[0].userID,
              changePasswordAtNextLogin: true,
            }),
          }
        );
        this.dialog!.innerHTML ="";
        this.dialog!.innerHTML = `Benutzer Passwort generiert. `;
        this.dialog!.setAttribute('style', 'border-color: #000000; border-width: 1px; font-size: 1.1em;');
        this.dialogText.style.color = "#000000";
        this.dialogText.style.padding = "0px 20%";
        this.emailText.innerHTML=`E-Mail: ${this.selectedUsers[0].userID}`;
        this.copyEmailButton.setAttribute("icon", "content_copy");
        this.emailTip.classList.add("tooltip");
        this.emailTipText.classList.add("tooltiptext");
        this.emailTipText.innerHTML = "E-Mail kopieren";
        this.emailTip.appendChild(this.copyEmailButton);
        this.emailTip.appendChild(this.emailTipText);
        this.emailDiv.appendChild(this.emailText);
        this.emailDiv.appendChild(this.emailTip);
        this.copyEmailButton.addEventListener('click', ()=> this.copyClipboard(e, "E-Mail",this.selectedUsers[0].userID));

        this.passwordText.innerHTML=`Initialpasswort: ${initialPassword}`
        this.copyPasswordButton.setAttribute("icon", "content_copy");
        this.passwordTip.classList.add("tooltip");
        this.passwordTipText.classList.add("tooltiptext");
        this.passwordTipText.innerHTML = "Passwort kopieren";
        this.passwordTip.appendChild(this.copyPasswordButton);
        this.passwordTip.appendChild(this.passwordTipText);
        this.passwordDiv.appendChild(this.passwordText);
        this.passwordDiv.appendChild(this.passwordTip);
        this.copyPasswordButton.addEventListener('click', ()=>this.copyClipboard(e, "Password",initialPassword));

        this.dialog!.appendChild(this.emailDiv);
        this.dialog!.appendChild(this.passwordDiv);
        this.footerButtonOk.setAttribute("slot", "primaryAction");
        this.footerButtonOk.setAttribute("dialogAction", "ok");
        this.footerButtonOk.innerText = "Ok";
        this.footerButtonOk.classList.add("raised");
        this.dialog!.appendChild(this.emailDiv);
        this.dialog!.appendChild(this.passwordDiv);
        this.dialog!.appendChild(this.footerButtonOk);
        //If dialog is closed then reset all values
        this.dialog!.addEventListener('closed', ()=>{
            this.tempSelectedRows.forEach((row:any) =>{
                row.selected = false;
              });
              this.selectedUsers = [];
              this.dialog!.innerHTML = "";
              this.emailDiv.innerHTML = "";
              this.passwordDiv.innerHTML = "";
              this.emailText.innerHTML = "";
              this.emailTip.innerHTML = "";
              this.passwordText.innerHTML = "";
              this.passwordTip.innerHTML = "";
        });
        this.dialog!.open = true;
      }
  };

  /**
   * Initialize new dialog
  */
  initDialog(){
    this.dialog!.innerHTML ="";
    this.footerButtonOk.innerHTML ="";
    this.footerButtonOk.setAttribute("slot", "primaryAction");
    this.footerButtonOk.setAttribute("dialogAction", "ok");
    this.footerButtonOk.innerText = "Ok";
    this.footerButtonOk.classList.add("raised");
    this.footerButtonCancel.setAttribute("slot", "secondaryAction");
    this.footerButtonCancel.setAttribute("dialogAction", "cancel");
    this.footerButtonCancel.innerText = "Abbrechen";
    this.footerButtonCancel.classList.add("raised");

    this.dialogText.style.color = "#000000";
    this.dialogText.style.padding = "0px 20%";
    this.dialog!.setAttribute('style', 'border-color: #000000; border-width: 1px; font-size: 1.1em;');
  }

  /**
   * Copy e-mail or password from the dialog to the clipboard
   * @param e 
   * @param type 
   * @param input 
   */
  copyClipboard = async (e:Event, type: string, input: string) =>{
    e.stopPropagation();
    //Switch case between e-mail and password
    switch(type){
        case "E-Mail": 
            //Copy e-mail to the clipboard
            navigator.clipboard.writeText(input);
            this.emailTipText.innerHTML = "";
            this.emailTipText.innerHTML = "E-Mail kopiert";
            //Changes the text of the email tip text if 3 sec. = 3000 msec. have passed
            setTimeout(() => {
                this.emailTipText.innerHTML = "E-Mail kopieren";
            }, 3000);
            break;
        case "Password": 
            //Copy password to the clipboard
            navigator.clipboard.writeText(input);
            this.passwordTipText.innerHTML = "";
            this.passwordTipText.innerHTML =  "Passwort kopiert";
            //Changes the text of the password tip text if 3 sec. = 3000 msec. have passed
            setTimeout(() => {
                this.passwordTipText.innerHTML = "Passwort kopieren";
            }, 3000);
            break;
    }
  };

  /**
   * Delete selected user 
   * @param e 
   */
  deleteUser = async (e: CustomEvent) => {
    e.preventDefault();
    /**
     * Access token for groups in admin.google
     */

    this.initDialog();
    //If more than one user got selected then put the text in dialog text
    if(this.selectedUsers.length > 1){
        this.dialogText.innerHTML="Sind Sie sicher, das Sie die ausgewählten Benutzer möchten?";
    //If only one user is selected then put the text in dialof text
    } else this.dialogText.innerHTML="Sind Sie sicher, das Sie den ausgewählte Benutzer löschen möchten?";
    //Click event for delete confirmation
     
    const footerButtonOkClickHandler = async ()=>{
      const accessToken = await this.getAccessToken(true, 
        "https://www.googleapis.com/auth/cloud-platform https://www.googleapis.com/auth/cloud-identity.groups https://www.googleapis.com/auth/admin.directory.group https://www.googleapis.com/auth/admin.directory.group.member https://www.googleapis.com/auth/admin.directory.user", 
        false);
        this.dialog!.innerHTML="";
        const okButton = document.createElement('mwc-button');
        okButton.setAttribute("dialogAction", "ok");
        okButton.innerText = "Ok";
        okButton.classList.add("raised");
        const circularProgress = document.createElement('mwc-circular-progress');
        circularProgress.setAttribute('density', '-1');
        circularProgress.setAttribute('indeterminate', 'true');
        //The shown text if more than one user are selected
        if(this.selectedUsers.length > 1){
            this.dialogText.innerHTML="Benutzer werden gelöscht..";
            this.dialogText.appendChild(circularProgress);
        //The shown text if only one user is selected
        } else {
            this.dialogText.innerHTML="Benutzer wird gelöscht..";
            this.dialogText.appendChild(circularProgress);
        }
        
        this.dialog!.style.textAlign="-webkit-center";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(okButton);
        this.dialog!.open=true;
        this.dialog!.addEventListener('closed', ()=>{
            okButton.remove();
        });
        /**
         * Selected user ids
         */
        const selectedUserIDs = this.selectedUsers.map((user: User) => user.userID);
        
        //Users will filtered based on the removed user(s)
        this.users = this.users.filter(
        (user: User) => !this.selectedUsers.includes(user)
        );
        //Delete users
        User.deleteUsers(this.database!, accessToken, selectedUserIDs).then(()=>{
          this.tempSelectedRows.forEach((row: any) => {
            row.selected = false;
          });
          this.dialog!.open = false;
          console.log("User deleted in spanner");
        });
        this.selectedUsers.forEach(async (user:User) =>{         
        //DELETE User from directory Users (All existing users)
        await fetchRetry(
            `https://admin.googleapis.com/admin/directory/v1/users/${user.userID}`,
            {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                }
            }
        ).then((response) => {
            //If it comes response then close dialog
            console.log(response);
            
            this.dialog!.innerHTML ="";
            console.log("User deleted in admin.google");
            this.selectedUsers = [];
            
            this.requestUpdate();
        })
        .catch((error) => console.log(error));
        });
    };
    this.footerButtonOk.addEventListener('click', footerButtonOkClickHandler);
    this.dialog!.appendChild(this.dialogText);
    this.dialog!.appendChild(this.footerButtonCancel);
    this.dialog!.appendChild(this.footerButtonOk);
    this.dialog!.open = true;
    this.dialog!.addEventListener('closed', ()=> {
        this.footerButtonOk.innerHTML ="";
        this.footerButtonOk.removeEventListener('click', footerButtonOkClickHandler); 
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-users": HitlUsers;
  }
}
